import { useTranslation } from 'react-i18next'
import { TrashIcon, DocumentDuplicateIcon, PencilIcon } from '@heroicons/react/24/outline'
import { Button } from 'src/components/button'
import { ListItemCard, ListItemRow } from 'src/components/layout/item-collection'
import type { ExportTemplate } from 'src/services/export-templates/models'
import type { ViewMode } from 'src/hooks/use-view-mode'
import { getTimeAgo } from 'src/utils/utils'

interface ExportTemplateItemProps {
  template: ExportTemplate
  viewMode: ViewMode
  onOpen: (template: ExportTemplate) => void
  onDuplicate: (template: ExportTemplate) => void
  onDelete: (template: ExportTemplate) => void
}

export const ExportTemplateItem = ({
  template,
  viewMode,
  onOpen,
  onDuplicate,
  onDelete,
}: ExportTemplateItemProps) => {
  const { t } = useTranslation()
  const organizationName = template.organizationRef?.name || ''
  const columnCount = template.columnConfig?.length ?? 0
  const createdAgo = template.created ? getTimeAgo(template.created) : ''
  const isCard = viewMode === 'card'
  const testIdSuffix = isCard ? '-card' : ''

  const actions = (
    <div className="flex shrink-0 items-center gap-1">
      <Button
        variant="ghost"
        tone="neutral"
        iconOnly
        size="sm"
        shape="square"
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onOpen(template)
        }}
        tooltip={t('exportTemplates.editTooltip')}
        data-testid={`edit-template-button${testIdSuffix}`}
      >
        <PencilIcon className="h-5 w-5" />
      </Button>
      <Button
        variant="ghost"
        tone="neutral"
        iconOnly
        size="sm"
        shape="square"
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onDuplicate(template)
        }}
        tooltip={t('exportTemplates.duplicateTooltip')}
        data-testid={`duplicate-template-button${testIdSuffix}`}
      >
        <DocumentDuplicateIcon className="h-5 w-5" />
      </Button>
      <Button
        variant="ghost"
        tone="error"
        iconOnly
        size="sm"
        shape="square"
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onDelete(template)
        }}
        tooltip={t('exportTemplates.deleteTooltip')}
        data-testid={`delete-template-button${testIdSuffix}`}
      >
        <TrashIcon className="h-5 w-5" />
      </Button>
    </div>
  )

  if (isCard) {
    return (
      <ListItemCard
        onClick={() => onOpen(template)}
        data-testid={`template-card-item-${template.id}`}
      >
        <div className="flex min-w-0 items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-lg font-bold text-text-primary" title={template.name}>
              {template.name}
            </h3>
            {organizationName && (
              <p className="truncate text-sm text-text-placeholder" title={organizationName}>
                {organizationName}
              </p>
            )}
          </div>
          {actions}
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <div className="min-w-0">
            <dt className="text-xs text-text-secondary">{t('exportTemplates.columnsLabel')}</dt>
            <dd className="truncate font-medium text-text-primary">{columnCount}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-text-secondary">{t('exportTemplates.createdLabel')}</dt>
            <dd className="truncate font-medium text-text-primary" title={createdAgo || undefined}>
              {createdAgo || '-'}
            </dd>
          </div>
        </dl>
      </ListItemCard>
    )
  }

  const subtitle = [
    organizationName,
    columnCount ? t('exportTemplates.columnsCount', { count: columnCount }) : '',
    createdAgo,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <ListItemRow onClick={() => onOpen(template)} data-testid={`template-list-item-${template.id}`}>
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-left font-semibold" title={template.name}>
          {template.name}
        </h3>
        {subtitle && (
          <p className="truncate text-sm text-text-placeholder" title={subtitle}>
            {subtitle}
          </p>
        )}
      </div>
      {actions}
    </ListItemRow>
  )
}
